const RECEIPT_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const STORAGE_PREFIX = "qolak:manual-transfer:pending:";

function storageKey(scope) {
  return `${STORAGE_PREFIX}${scope}`;
}

function browserSessionStorage() {
  try {
    return window.sessionStorage;
  } catch (_error) {
    return null;
  }
}

function readPending(scope, storage) {
  try {
    const raw = storage?.getItem(storageKey(scope));
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function writePending(scope, storage, pending) {
  try {
    if (!storage) return false;
    storage.setItem(storageKey(scope), JSON.stringify(pending));
    return true;
  } catch (_error) {
    return false;
  }
}

function deletePending(scope, storage) {
  try {
    storage?.removeItem(storageKey(scope));
    return true;
  } catch (_error) {
    return false;
  }
}

function normalizePayload(payload) {
  return Object.fromEntries(Object.entries(payload).sort(([first], [second]) => first.localeCompare(second)));
}

function samePayload(first, second) {
  return JSON.stringify(normalizePayload(first)) === JSON.stringify(normalizePayload(second));
}

function newRequestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) throw new Error("مرورگر امکان ثبت مطمئن درخواست را ندارد؛ از مرورگر به‌روز استفاده کنید");
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  return `qolak-${Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("")}`;
}

export function validateReceiptFiles(files) {
  const selected = Array.from(files || []);
  if (selected.length === 0) throw new Error("رسید واریز را انتخاب کنید");
  selected.forEach(file => {
    const name = String(file?.name || "").toLowerCase();
    const extension = name.includes(".") ? name.split(".").pop() : "";
    if (!RECEIPT_EXTENSIONS.has(extension)) {
      throw new Error("فرمت فیش باید JPG، PNG یا WebP باشد");
    }
  });
  return selected;
}

async function receiptDigest(file) {
  const bytes=await new Promise((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(reader.result);reader.onerror=()=>reject(new Error("خواندن فیش ممکن نشد"));reader.readAsArrayBuffer(file);});
  if(!globalThis.crypto?.subtle)throw new Error("مرورگر امکان بررسی مطمئن فیش را ندارد");
  const digest=await crypto.subtle.digest("SHA-256",new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest),value=>value.toString(16).padStart(2,"0")).join("");
}

export function createReceiptRetryState(scope, storage = browserSessionStorage()) {
  if (!scope) throw new Error("شناسه حساب برای ثبت سفارش آماده نیست");
  let pending = readPending(scope, storage);
  let busy = false;

  const persist = next => {
    pending = next;
    writePending(scope, storage, next);
  };

  return {
    beginAttempt(payload, receiptNames, receiptDigests) {
      const attempt = {
        client_request_id: newRequestId(),
        payload: normalizePayload(payload),
        receipt_names: receiptNames,
        receipt_digests: receiptDigests,
        uploaded_count: 0,
      };
      // Save the recovery key before any request can create a financial record.
      if (!writePending(scope, storage, attempt)) {
        throw new Error("ذخیرهٔ وضعیت درخواست در مرورگر ممکن نیست؛ تنظیمات ذخیره‌سازی مرورگر را بررسی کنید و دوباره تلاش کنید");
      }
      pending = attempt;
      window.dispatchEvent(new Event("qolak-financial-attempt"));
      return pending;
    },
    attachOrder(order) {
      if (!order?.tracking_code) throw new Error("شماره پیگیری سفارش دریافت نشد");
      persist({...pending, tracking_code: order.tracking_code});
      return pending;
    },
    current() {
      return pending;
    },
    markUploaded(count) {
      persist({...pending, uploaded_count: count});
    },
    clear() {
      const completed = pending;
      pending = null;
      deletePending(scope, storage);
      return completed;
    },
    isBusy() {
      return busy;
    },
    setBusy(value) {
      busy = value;
    },
  };
}

export async function submitManualTransfer({
  api,
  state,
  payload,
  file,
  files,
  createPath = "/user/buy",
  receiptPath = "/user/buy/receipt",
}) {
  if (state.isBusy()) throw new Error("در حال ثبت درخواست قبلی هستیم؛ لطفاً چند لحظه صبر کنید");
  const paymentMethod = payload.payment_method;
  const selectedFiles = paymentMethod === "bank_transfer" ? validateReceiptFiles(files || (file ? [file] : [])) : [];
  state.setBusy(true);
  try {
    let pending = state.current();
    const retrying = Boolean(pending);
    const receiptNames = selectedFiles.map(selected => selected.name);
    const receiptDigests = await Promise.all(selectedFiles.map(receiptDigest));
    if (pending && !pending.tracking_code && payload.expected_price_per_gram_rial !== undefined && pending.payload.expected_price_per_gram_rial === undefined) {
      const original={...payload};delete original.expected_price_per_gram_rial;
      if(!samePayload(original,pending.payload))throw new Error("اطلاعات درخواست قبلی را بدون تغییر پیگیری کنید");
      const flow={"/user/buy":"silver_buy","/user/buy-bar":"bar_buy","/user/buy-gold":"gold_buy","/user/wallet/charge":"wallet_charge"}[createPath];
      const lookup=await api("GET",`/user/request-status/${flow}/${pending.client_request_id}`);
      if(!lookup?.committed)throw new Error("ثبت درخواست قدیمی هنوز تأیید نشده؛ شناسه و مدارک را برای بررسی به پشتیبانی ارائه کنید. واریز دیگری لازم نیست.");
      pending=state.attachOrder(lookup.transaction);
    }
    if (pending) {
      if (!samePayload(pending.tracking_code && pending.payload.expected_price_per_gram_rial === undefined ? Object.fromEntries(Object.entries(payload).filter(([key])=>key!=="expected_price_per_gram_rial")) : payload, pending.payload) || !samePayload(receiptNames, pending.receipt_names) || (pending.receipt_digests && !samePayload(receiptDigests, pending.receipt_digests))) {
        throw new Error("این سفارش در انتظار فیش است؛ مقدار، روش پرداخت و فایل‌ها را بدون تغییر دوباره ارسال کنید");
      }
    } else {
      pending = state.beginAttempt(payload, receiptNames, receiptDigests);
    }

    let order = pending.tracking_code ? {tracking_code: pending.tracking_code} : null;
    if (!order) {
      try {
        order = await api("POST", createPath, {...payload, client_request_id: pending.client_request_id});
        state.attachOrder(order);
      } catch (error) {
        // Timeouts, throttling and lost responses may follow a committed request.
        if ([400, 404, 405, 413, 415, 422, 428].includes(error?.status) || error?.code === "quote_changed" || ["GOAL_TARGET_EXCEEDED","GOAL_METAL_NOT_SELECTED","GOAL_WEIGHT_INVALID","COPPER_MARKET_CLOSED","INSUFFICIENT_FUNDS","WALLET_MISSING"].includes(error?.code)) state.clear();
        throw error;
      }
    }

    if (paymentMethod === "bank_transfer") {
      const uploadedCount = state.current()?.uploaded_count || 0;
      for (let index = uploadedCount; index < selectedFiles.length; index += 1) {
        const form = new FormData();
        form.append("tracking_code", order.tracking_code);
        form.append("receipt", selectedFiles[index]);
        await api("POST", receiptPath, form, true);
        state.markUploaded(index + 1);
      }
    }
    state.clear();
    return {order, retrying};
  } finally {
    state.setBusy(false);
  }
}
